import { useEffect, useState } from 'react'

import { useApiPostKirimPenilaianTahunanDinas } from 'api/kinerja/kepala-sekolah/dinas/penilaian-tahunan'
import { useApiPostKirimPenilaianTahunanDinasTabGuru } from 'api/kinerja/kepala-sekolah/guru/dinas/penilaian-tahunan'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type { TabFilterRoles } from 'types/kinerja-ks'
import type { TPenilaianTahunan } from 'types/kinerja-dinas-penilaian'

type AcceptableTabFilterRoles = TabFilterRoles | 'ALL'

type TProps = {
  includeGuru?: boolean
  onSuccess: ({ count }: { count: number }) => void
  onError: ({ type }: { type: AcceptableTabFilterRoles }) => void
}

const usePenetapanTahunanKirimDinas = ({
  includeGuru = false,
  onSuccess,
  onError,
}: TProps) => {
  const { periodYear } = usePeriodContext()

  const [response, setResponse] = useState<{
    settled: TabFilterRoles[]
    success: number
    errors: TabFilterRoles[]
    expectedSettledLength?: number
  }>({ settled: [], success: 0, errors: [], expectedSettledLength: 0 })

  // Mutations for Kepala Sekolah
  const {
    mutate: mutateKs,
    mutateAsync: mutateAsyncKs,
    isLoading: isMutationLoadingKs,
    isError: isMutationErrorKs,
    isSuccess: isMutationSuccessKs,
  } = useApiPostKirimPenilaianTahunanDinas({ year: periodYear })

  // Mutations for Guru
  const {
    mutate: mutateGuru,
    mutateAsync: mutateAsyncGuru,
    isLoading: isMutationLoadingGuru,
    isError: isMutationErrorGuru,
    isSuccess: isMutationSuccessGuru,
  } = useApiPostKirimPenilaianTahunanDinasTabGuru({ year: periodYear })

  const isMutationLoading = isMutationLoadingKs || isMutationLoadingGuru
  const isMutationError = isMutationErrorKs || isMutationErrorGuru
  const isMutationSuccess = isMutationSuccessKs || isMutationSuccessGuru

  const handleError = (errors: TabFilterRoles[]) => {
    let type: AcceptableTabFilterRoles = 'ALL'

    if (errors.length === 2) type = 'ALL'
    else if (errors.includes('KS')) type = 'KS'
    else if (errors.includes('GURU')) type = 'GURU'

    onError({ type })
  }

  const mutate = async (
    itemsKs: TPenilaianTahunan[],
    itemsGuru: TPenilaianTahunan[]
  ) => {
    if (!!itemsKs && itemsKs.length > 0) {
      setResponse((prev) => {
        return {
          ...prev,
          expectedSettledLength: prev.expectedSettledLength + 1,
        }
      })

      mutateKs(
        { id: [...itemsKs?.map((item) => `${item.id}`)] },
        {
          onError: () => {
            setResponse((prev) => {
              return {
                ...prev,
                errors: [...prev.errors, 'KS'],
              }
            })
          },
          onSuccess: (res) =>
            setResponse((prev) => {
              return {
                ...prev,
                success: prev.success + (res?.data?.totalSent || 0),
              }
            }),
          onSettled: () => {
            setResponse((prev) => {
              return {
                ...prev,
                settled: [...prev.settled, 'KS'],
              }
            })
          },
        }
      )
    }

    if (includeGuru && !!itemsGuru && itemsGuru.length > 0) {
      setResponse((prev) => {
        return {
          ...prev,
          expectedSettledLength: prev.expectedSettledLength + 1,
        }
      })

      mutateGuru(
        { id: [...itemsGuru?.map((item) => item.id)] },
        {
          onError: () =>
            setResponse((prev) => {
              return {
                ...prev,
                errors: [...prev.errors, 'GURU'],
              }
            }),
          onSuccess: (res) =>
            setResponse((prev) => {
              return {
                ...prev,
                success: prev.success + (res?.data?.totalSent || 0),
              }
            }),
          onSettled: () => {
            setResponse((prev) => {
              return {
                ...prev,
                settled: [...prev.settled, 'GURU'],
              }
            })
          },
        }
      )
    }
  }

  useEffect(() => {
    if (
      response.expectedSettledLength > 0 &&
      response.expectedSettledLength === response.settled.length
    ) {
      if (response.errors.length > 0) handleError(response.errors)
      else onSuccess({ count: response.success })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response.settled])

  return {
    // Kepala Sekolah
    mutateAsyncKs,
    isMutationLoadingKs,
    isMutationErrorKs,
    isMutationSuccessKs,

    // Guru
    mutateAsyncGuru,
    isMutationLoadingGuru,
    isMutationErrorGuru,
    isMutationSuccessGuru,

    // Combined
    isMutationLoading,
    isMutationError,
    isMutationSuccess,

    mutate,
  }
}

export default usePenetapanTahunanKirimDinas
